import React, { useState, useCallback } from 'react';
import { bool, func, object, node, number, string } from 'prop-types';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';

import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
// import { propTypes } from '../../../util/types';
import { createResourceLocatorString } from '../../../util/routes';

import ModalInMobile from '../../../components/ModalInMobile/ModalInMobile';
import Button from '../../../components/Button/Button';

import PopupOpenerButton from '../PopupOpenerButton/PopupOpenerButton';
import css from './SearchFiltersMobile.module.css';

function SearchFiltersMobile({
  rootClassName = null,
  className = null,
  children,
  sortByComponent = null,
  listingsAreLoaded,
  resultsCount = null,
  searchInProgress = false,
  showAsModalMaxWidth,
  onMapIconClick = () => {},
  onManageDisableScrolling,
  onOpenModal,
  onCloseModal,
  resetAll,
  selectedFiltersCount = 0,
  noResultsInfo,
  isMapVariant = true,
  urlQueryParams,
}) {
  const [isFiltersOpenOnMobile, setIsFiltersOpenOnMobile] = useState(false);
  const [initialQueryParams, setInitialQueryParams] = useState(null);
  const routeConfiguration = useRouteConfiguration();
  const navigate = useNavigate();

  // Open filters modal, set the initial parameters to current ones
  const openFilters = useCallback(() => {
    onOpenModal();
    setIsFiltersOpenOnMobile(true);
    setInitialQueryParams(urlQueryParams);
  }, [onOpenModal, urlQueryParams]);

  // Close the filters by clicking cancel, revert to the initial params
  const cancelFilters = useCallback(() => {
    navigate(
      createResourceLocatorString('SearchPage', routeConfiguration, {}, initialQueryParams)
    );
    onCloseModal();
    setIsFiltersOpenOnMobile(false);
    setInitialQueryParams(null);
  }, [navigate, onCloseModal, routeConfiguration, initialQueryParams]);

  // Close the filter modal
  const closeFilters = useCallback(() => {
    onCloseModal();
    setIsFiltersOpenOnMobile(false);
  }, [onCloseModal]);

  // Reset all filter query parameters
  const handleResetAll = useCallback(
    e => {
      resetAll(e);
      if (e && e.currentTarget) {
        e.currentTarget.blur();
      }
    },
    [resetAll]
  );

  const classes = classNames(rootClassName || css.root, className);

  // Inline i18n
  const resultsFound = `Found ${resultsCount} results`;
  const noResults = 'No results';
  const loadingResults = 'Loading results...';
  const filtersHeading = 'Filters';
  const modalCloseButtonMessage = 'Cancel';
  const showListingsLabel = `Show ${resultsCount} listings`;
  const filtersButtonLabel = 'Filters';
  const openMapViewLabel = 'Open map view';
  const resetAllLabel = 'Reset all';

  return (
    <div className={classes}>
      <div className={css.searchResultSummary}>
        {listingsAreLoaded && resultsCount > 0 ? resultsFound : null}
        {listingsAreLoaded && resultsCount === 0 ? noResults : null}
        {searchInProgress ? loadingResults : null}
      </div>
      <div className={css.buttons}>
        <PopupOpenerButton isSelected={selectedFiltersCount > 0} toggleOpen={openFilters}>
          <span className={css.mapIconText}>{filtersButtonLabel}</span>
        </PopupOpenerButton>
        {sortByComponent}
        {isMapVariant ? (
          <div className={css.mapIcon} onClick={onMapIconClick}>
            <span className={css.mapIconText}>{openMapViewLabel}</span>
          </div>
        ) : null}
      </div>
      {noResultsInfo ? noResultsInfo : null}
      <ModalInMobile
        id="SearchFiltersMobile.filters"
        isModalOpenOnMobile={isFiltersOpenOnMobile}
        onClose={cancelFilters}
        showAsModalMaxWidth={showAsModalMaxWidth}
        onManageDisableScrolling={onManageDisableScrolling}
        containerClassName={css.modalContainer}
        closeButtonMessage={modalCloseButtonMessage}
      >
        <div className={css.modalHeadingWrapper}>
          <span className={css.modalHeading}>{filtersHeading}</span>
          <button className={css.resetAllButton} onClick={handleResetAll}>
            {resetAllLabel}
          </button>
        </div>
        {isFiltersOpenOnMobile ? <div className={css.filtersWrapper}>{children}</div> : null}
        <div className={css.showListingsContainer}>
          <Button className={css.showListingsButton} onClick={closeFilters}>
            {showListingsLabel}
          </Button>
        </div>
      </ModalInMobile>
    </div>
  );
}

SearchFiltersMobile.propTypes = {
  rootClassName: string,
  className: string,
  urlQueryParams: object.isRequired,
  sortByComponent: node,
  listingsAreLoaded: bool.isRequired,
  resultsCount: number,
  searchInProgress: bool,
  showAsModalMaxWidth: number.isRequired,
  onMapIconClick: func,
  onManageDisableScrolling: func.isRequired,
  onOpenModal: func.isRequired,
  onCloseModal: func.isRequired,
  resetAll: func.isRequired,
  selectedFiltersCount: number,
  isMapVariant: bool,
  noResultsInfo: node,
  children: node.isRequired,
};

export default SearchFiltersMobile;
